import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/** Requires the user to be logged in. Redirects to `fallback` otherwise. */
export const ProtectedRoute: React.FC<{ children: React.ReactElement; fallback?: string }> = ({
  children,
  fallback = '/login/',
}) => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to={fallback} replace />;
};

/** Only accessible when NOT logged in. Redirects logged-in users by subscription status. */
export const GuestRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isLoggedIn, hasSubscription, isAdmin, authLoading } = useAuth();
  if (authLoading) return null;
  return isLoggedIn ? <Navigate to={isAdmin ? '/live-classes/' : hasSubscription ? '/account/' : '/pricing/'} replace /> : children;
};

/** Requires login + active subscription; redirects to /pricing/ otherwise. */
export const SubscriptionRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isLoggedIn, hasSubscription, isAdmin, authLoading } = useAuth();
  if (authLoading) return null;
  return isLoggedIn && hasSubscription && !isAdmin ? children : <Navigate to={isAdmin ? '/live-classes/' : '/pricing/'} replace />;
};

/** Requires an authenticated administrator. */
export const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isLoggedIn, isAdmin, authLoading } = useAuth();
  if (authLoading) return null;
  return isLoggedIn && isAdmin ? children : <Navigate to="/account/" replace />;
};

/** Blocks pricing for users who already have active subscription. */
export const NoSubscriptionOnlyRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isLoggedIn, hasSubscription, isAdmin, authLoading } = useAuth();
  if (authLoading) return null;
  return isLoggedIn && (isAdmin || hasSubscription) ? <Navigate to={isAdmin ? '/live-classes/' : '/account/'} replace /> : children;
};
